import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";

import { api, formatApiError } from "@/lib/api";
import { StepBuilder } from "@/components/StepBuilder";
import { VariableEditor } from "@/components/VariableEditor";
import { TagPicker, TAG_CHOICES } from "@/components/TagPicker";
import { useAuth } from "@/lib/auth.jsx";

const KEY_RE = /^[a-z_][a-z0-9_]*$/;

const blankForm = {
  name: "",
  description: "",
  tags: [],
  variables: [],
  steps: [],
};

export default function WorkflowEditorPage({ mode = "create" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = mode === "edit";
  const { hasPermission, loading: authLoading } = useAuth();
  const canWrite = hasPermission("workflow.write");

  useEffect(() => {
    if (!authLoading && !canWrite) {
      navigate("/workflows", { replace: true });
    }
  }, [authLoading, canWrite, navigate]);

  const [form, setForm] = useState(blankForm);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const { data: current, isLoading } = useQuery({
    queryKey: ["workflow", id],
    queryFn: async () => {
      const { data } = await api.get(`/workflows/${id}`);
      return data;
    },
    enabled: isEdit && !!id,
  });

  useEffect(() => {
    if (isEdit && current) {
      setForm({
        name: current.name,
        description: current.description,
        tags: current.tags ?? [],
        variables: current.variables ?? [],
        steps: current.steps ?? [],
      });
    }
  }, [isEdit, current]);

  const validationErrors = useMemo(() => validate(form), [form]);
  const canSubmit = Object.keys(validationErrors).length === 0 && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    setErrorMsg(null);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        tags: form.tags,
        variables: form.variables.map((v) => ({
          key: v.key.trim(),
          label: v.label.trim(),
          default: v.default || "",
          required: !!v.required,
          description: v.description || "",
        })),
        steps: form.steps.map((s, idx) => ({
          ...s,
          order: idx + 1,
        })),
      };
      const url = isEdit ? `/workflows/${id}` : "/workflows";
      const method = isEdit ? "put" : "post";
      const { data } = await api[method](url, payload);
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
      queryClient.invalidateQueries({ queryKey: ["workflow", data.workflow_id] });
      queryClient.invalidateQueries({ queryKey: ["versions", data.workflow_id] });
      toast.success(
        isEdit
          ? `Saved as v${data.current_version}`
          : `Created "${data.name}"`,
      );
      navigate(`/workflows/${data.workflow_id}`);
    } catch (err) {
      setErrorMsg(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (isEdit && isLoading) {
    return <Skeleton className="h-96 w-full rounded-xl" data-testid="editor-loading" />;
  }

  const nextVersion = isEdit && current ? current.current_version + 1 : 1;

  return (
    <form className="space-y-6" onSubmit={handleSubmit} data-testid="workflow-editor-form">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Link
            to={isEdit ? `/workflows/${id}` : "/workflows"}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900"
            data-testid="editor-back-link"
          >
            <ArrowLeft size={14} />
            {isEdit ? "Back to workflow" : "Back to library"}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
            {isEdit ? "Edit workflow" : "New workflow"}
          </h1>
          {isEdit && current && (
            <p
              className="mt-1 text-sm text-slate-500"
              data-testid="editor-version-hint"
            >
              Currently on <span className="font-mono">v{current.current_version}</span> · saving will create{" "}
              <span className="font-mono font-semibold text-indigo-700">v{nextVersion}</span>
            </p>
          )}
        </div>
        <Button
          type="submit"
          disabled={!canSubmit}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="editor-save-button"
        >
          <Save size={16} className="mr-1.5" />
          {submitting ? "Saving…" : isEdit ? "Save as new version" : "Create workflow"}
        </Button>
      </header>

      {errorMsg && (
        <Alert variant="destructive" data-testid="editor-error">
          <AlertDescription>{errorMsg}</AlertDescription>
        </Alert>
      )}

      <Section title="Basics" subtitle="Identify and categorise this workflow.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
          <div className="md:col-span-7">
            <Label htmlFor="wf-name">Name</Label>
            <Input
              id="wf-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Create Requisition Demo"
              data-testid="workflow-name-input"
              className={validationErrors.name ? "border-red-400" : ""}
            />
            {validationErrors.name && (
              <p className="mt-1 text-[11px] text-red-600" data-testid="workflow-name-error">
                {validationErrors.name}
              </p>
            )}
          </div>
        </div>
        <div>
          <Label htmlFor="wf-desc">Description</Label>
          <Textarea
            id="wf-desc"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="What does this workflow demonstrate?"
            rows={3}
            data-testid="workflow-description-input"
          />
        </div>
        <div>
          <Label>Tags</Label>
          <TagPicker
            value={form.tags}
            onChange={(tags) => setForm((f) => ({ ...f, tags }))}
            testid="workflow-tag-picker"
          />
        </div>
      </Section>

      <Section
        title="Variables"
        subtitle="Inputs that can be interpolated into steps via ${var_name}."
      >
        <VariableEditor
          value={form.variables}
          onChange={(variables) => setForm((f) => ({ ...f, variables }))}
        />
        {validationErrors.variables && (
          <p className="mt-1 text-[11px] text-red-600" data-testid="variables-error">
            {validationErrors.variables}
          </p>
        )}
      </Section>

      <Section
        title="Steps"
        subtitle="The ordered actions the agent executes. Drafts with empty steps are allowed."
      >
        <StepBuilder
          value={form.steps}
          onChange={(steps) => setForm((f) => ({ ...f, steps }))}
        />
        {validationErrors.steps && (
          <p className="mt-1 text-[11px] text-red-600" data-testid="steps-error">
            {validationErrors.steps}
          </p>
        )}
      </Section>
    </form>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div>
        <h2 className="text-base font-semibold tracking-tight text-slate-900">{title}</h2>
        {subtitle && (
          <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        )}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function validate(form) {
  const errors = {};
  const name = (form.name || "").trim();
  if (name.length < 3) errors.name = "Name must be at least 3 characters.";
  if (name.length > 120) errors.name = "Name must be 120 characters or fewer.";

  const keys = new Set();
  for (const v of form.variables) {
    const k = (v.key || "").trim();
    if (!k && !v.label) continue; // skip blank rows
    if (!KEY_RE.test(k)) {
      errors.variables = `Variable key "${k}" must match a-z, 0-9, _ (snake_case).`;
      break;
    }
    if (keys.has(k)) {
      errors.variables = `Duplicate variable key: ${k}`;
      break;
    }
    keys.add(k);
    if (!v.label || !v.label.trim()) {
      errors.variables = `Variable "${k}" is missing a label.`;
      break;
    }
  }

  for (let i = 0; i < form.steps.length; i++) {
    const s = form.steps[i];
    if (!s.name || !s.name.trim()) {
      errors.steps = `Step ${i + 1} is missing a name.`;
      break;
    }
    if (s.action === "goto" && !s.url) {
      errors.steps = `Step ${i + 1} ("${s.name}") needs a URL.`;
      break;
    }
    if ((s.action === "fill" || s.action === "click") && !s.selector) {
      errors.steps = `Step ${i + 1} ("${s.name}") needs a selector.`;
      break;
    }
    if (s.action === "fill" && (s.value === undefined || s.value === null)) {
      errors.steps = `Step ${i + 1} ("${s.name}") needs a value.`;
      break;
    }
    if (s.action === "screenshot" && !s.screenshot_name) {
      errors.steps = `Step ${i + 1} ("${s.name}") needs a screenshot name.`;
      break;
    }
    if (s.action === "assertion" && !s.assertion?.expected) {
      errors.steps = `Step ${i + 1} ("${s.name}") needs an expected value.`;
      break;
    }
  }

  return errors;
}
